import { formTemplatePickerOpenAtom } from '@/atoms/chatAtoms';
import { formFillingUploadModalOpenAtom } from '@/atoms/formFillingAtom';
import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyP2Bold } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { useSetAtom } from 'jotai';
import { CircleCheck, FilePlus, FileText } from 'lucide-react';
interface FormFillNeedsTemplateBlockProps {
  title: string;
  /** Derived from timeline: true when a subsequent form_fill entry exists (user completed the action). Survives page reload. */
  isActioned?: boolean;
}

export function FormFillNeedsTemplateBlock({ title, isActioned }: Readonly<FormFillNeedsTemplateBlockProps>) {
  const { t } = useTranslations();
  const setUploadOpen = useSetAtom(formFillingUploadModalOpenAtom);
  const setPickerOpen = useSetAtom(formTemplatePickerOpenAtom);

  if (isActioned) {
    return (
      <div className="flex w-full items-center gap-3 rounded-lg border border-border bg-surface-accent p-4 transition-all duration-200">
        <FileText className="size-5 shrink-0 text-text-tertiary" aria-hidden="true" />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyP2Bold className="truncate text-text-primary">{t('chat.formFillNeedsTemplate.forms')}</TypographyP2Bold>
          <div className="flex items-center gap-1.5">
            <CircleCheck className="size-3 text-validation-success" />
            <TypographyCaption className="text-text-tertiary">
              {t('chat.formFillNeedsTemplate.actionCompleted')}
            </TypographyCaption>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full items-center gap-3 rounded-lg border border-border bg-surface-accent p-4 transition-all duration-200">
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <TypographyP2Bold className="truncate text-text-primary">{title}</TypographyP2Bold>
        <TypographyCaption className="text-text-tertiary">
          {t('chat.formFillNeedsTemplate.description', {
            defaultMessage:
              'Attach the blank PDF or Word form to chat, pick a saved form, or upload one to your library, then Heidi can fill it from your answers.',
          })}
        </TypographyCaption>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => setUploadOpen(true)}>
            <FilePlus className="mr-1.5 size-4" />
            {t('chat.formFillNeedsTemplate.createNewTemplate')}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
            <FileText className="mr-1.5 size-4" />
            {t('chat.formFillNeedsTemplate.selectFromTemplates')}
          </Button>
        </div>
      </div>
    </div>
  );
}
